import React from 'react';
import { Tag } from 'antd';
import { PhoneOutlined, CheckSquareOutlined, SyncOutlined } from '@ant-design/icons';
import './TaskActivityPrimaryCell.css';
import { coerceEnumByLabel } from '../../../lib/api/coerceEnum';
import type { TaskItemListItem, TaskPriority } from '../types';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
  TASK_TYPE_SHORT_LABELS,
  TASK_TYPE_TAG_COLORS,
  TASK_TYPE_FOLLOWUP,
  TASK_TYPE_RECURRING,
  normalizeTaskStatus,
  normalizeTaskType,
} from '../constants';

const TASK_PRIORITY_BY_LABEL = Object.fromEntries(
  Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => [label, Number(value) as TaskPriority]),
) as Record<string, TaskPriority>;

export interface TaskActivityPrimaryCellProps {
  record: TaskItemListItem;
  onViewTitle: (taskId: string) => void;
  /** Rendered before type / priority / status (e.g. queue bucket on Task Dashboard). */
  prefixTags?: React.ReactNode;
}

const tagStyle = { marginInlineEnd: 0, fontSize: 11 } as const;

const TASK_TYPE_ICONS: Record<number, React.ReactNode> = {
  [TASK_TYPE_FOLLOWUP]: <PhoneOutlined />,
  [TASK_TYPE_RECURRING]: <SyncOutlined />,
};

export function TaskActivityPrimaryCell({
  record,
  onViewTitle,
  prefixTags,
}: TaskActivityPrimaryCellProps): React.ReactElement {
  const taskType = normalizeTaskType(record);
  const priority = coerceEnumByLabel(record.priority, TASK_PRIORITY_BY_LABEL) ?? 1;
  const status = normalizeTaskStatus(record.status);

  return (
    <div className="cp-task-activity-cell">
      <a className="cp-task-activity-cell__title" onClick={() => onViewTitle(record.id)}>
        {record.title}
      </a>
      <div className="cp-task-activity-cell__tags">
        {prefixTags}
        <Tag color={TASK_TYPE_TAG_COLORS[taskType]} style={tagStyle}>
          {TASK_TYPE_ICONS[taskType] ?? <CheckSquareOutlined />}
          {TASK_TYPE_SHORT_LABELS[taskType]}
        </Tag>
        <Tag color={TASK_PRIORITY_COLORS[priority]} style={tagStyle}>
          {TASK_PRIORITY_LABELS[priority]}
        </Tag>
        <Tag color={TASK_STATUS_COLORS[status]} style={tagStyle}>
          {TASK_STATUS_LABELS[status]}
        </Tag>
      </div>
    </div>
  );
}

export default TaskActivityPrimaryCell;
